import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Search,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/cn";

// Augmenta ColumnMeta para colgar utilidades de presentación de cada columna: `className`
// se aplica tanto a la cabecera como a la celda (p. ej. ocultar en móvil con
// "hidden sm:table-cell" o alinear a la derecha). Reutilizable por todas las tablas de
// maestros migradas a react-table (#34).
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
    /**
     * Marca una columna cuyas celdas NO deben disparar el `onRowClick` de la fila: la celda
     * detiene la propagación del clic (p. ej. la columna de acciones con sus botones, o
     * cualquier celda con controles interactivos como toggles o enlaces). Así el clic en un
     * botón de acción nunca navega/abre el modal de la fila.
     */
    stopRowClick?: boolean;
  }
}

type DataTableProps<TData> = {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Placeholder del buscador global reactivo (filtra sobre todas las columnas de texto). */
  searchPlaceholder?: string;
  /**
   * Oculta el buscador global en memoria. Lo usan los listados que adoptaron `FiltrosBar`
   * (#247): allí el texto libre lo filtra el SERVIDOR sobre TODAS las filas, no solo sobre
   * las ya descargadas, así que un segundo buscador local sería engañoso. La tabla conserva
   * la ordenación y la paginación de cliente.
   */
  sinBuscador?: boolean;
  /**
   * Fila vacía cuando no hay filas que mostrar: porque el buscador local no deja resultados
   * o, en los listados con `sinBuscador`, porque no los dejan los filtros del SERVIDOR de la
   * página (#247) —o porque la lista viene vacía—.
   */
  emptyMessage?: string;
  /** Clase CSS por fila derivada del dato (p. ej. resaltar un punto bloqueado). */
  rowClassName?: (row: TData) => string | undefined;
  /**
   * Acción principal de la fila: al pulsar en cualquier parte de la fila (salvo las celdas
   * marcadas con `meta.stopRowClick`) se ejecuta con el dato de esa fila. Se usa para navegar
   * al detalle (LOTOs) o abrir el modal de edición (maestros). La fila NO se hace focusable
   * por teclado: cada fila conserva su vía accesible propia (el `<Link>` del número en LOTOs o
   * los botones de acción en los maestros), que siguen siendo tabulables.
   */
  onRowClick?: (row: TData) => void;
  initialSorting?: SortingState;
  pageSize?: number;
};

/**
 * DataTable es la tabla reutilizable de los maestros de fábrica (#34) sobre
 * @tanstack/react-table: ordenación por columna, buscador global reactivo y paginación. Se
 * apoya en las primitivas visuales de components/ui/table para no romper el estilo shadcn
 * existente. Es "mobile-first": el buscador ocupa el ancho en móvil y se alinea desde `sm`, y
 * cada columna decide su visibilidad con `meta.className`. Los filtros de los listados son
 * del SERVIDOR y viven en la `FiltrosBar` de la página (#247), no en la tabla.
 */
export function DataTable<TData>({
  columns,
  data,
  searchPlaceholder = "Buscar…",
  sinBuscador = false,
  emptyMessage = "No hay resultados.",
  rowClassName,
  onRowClick,
  initialSorting = [],
  pageSize = 10,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const rows = table.getRowModel().rows;
  const totalPaginas = table.getPageCount();

  return (
    <div className="flex flex-col gap-3">
      {/* Barra de herramientas: solo el buscador global. Los listados que ya filtran en el
          servidor (#247) la omiten entera pasando `sinBuscador`: sus controles viven en la
          FiltrosBar de la página. */}
      {sinBuscador ? null : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search
              className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-slate-400"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label="Buscar"
              placeholder={searchPlaceholder}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const puedeOrdenar = header.column.getCanSort();
                const orden = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    className={header.column.columnDef.meta?.className}
                    aria-sort={
                      orden === "asc" ? "ascending" : orden === "desc" ? "descending" : undefined
                    }
                  >
                    {header.isPlaceholder ? null : puedeOrdenar ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 font-semibold text-slate-500 uppercase tracking-wide hover:text-slate-700"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {orden === "asc" ? (
                          <ChevronUp className="h-4 w-4" aria-hidden="true" />
                        ) : orden === "desc" ? (
                          <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 text-slate-300" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="py-10 text-center text-slate-500">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  onRowClick && "cursor-pointer hover:bg-primary-50/60",
                  rowClassName?.(row.original),
                )}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cell.column.columnDef.meta?.className}
                    // Las celdas marcadas con `stopRowClick` (acciones, controles interactivos)
                    // detienen la propagación para no disparar el `onRowClick` de la fila.
                    onClick={
                      cell.column.columnDef.meta?.stopRowClick
                        ? (e) => e.stopPropagation()
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Paginación: solo cuando hay más de una página. */}
      {totalPaginas > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-slate-500 text-sm">
            Página {table.getState().pagination.pageIndex + 1} de {totalPaginas}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Página anterior"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Página siguiente"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
